export interface Innovation {
  id: string;
  title: string;
  submittedBy: string;
  submittedByType: 'individual' | 'institution';
  institution?: string;
  submissionDate: string;
  status: 'pending-assessment' | 'assessment-completed' | 'ip-filed';
  category: string;
  keywords: string[];
  stage: 'prototype' | 'pilot' | 'market-ready';
  description: string;
  targetUsers: string;
  documentUrl?: string;
  reviewerId?: string;
  reviewerName?: string;
  decision?: 'has-potential' | 'no-potential';
  ipProtectionTypes?: ('Patent' | 'Trademark' | 'Copyright' | 'Industrial Design')[];
  ipProtectionRecommendation?: string;
  improvementRecommendations?: string;
  assessmentMethod?: 'ai' | 'manual';
  aiRelevanceScore?: number;
  filingRequest?: {
    selectedTypes: ('Patent' | 'Trademark' | 'Copyright' | 'Industrial Design')[];
    notifiedToReviewer: boolean;
    notificationDate?: string;
  };
}

export const mockInnovations: Innovation[] = [
  {
    id: 'INV-2024-001',
    title: 'AI-Powered Diagnostic Tool for Early Cancer Detection',
    submittedBy: 'Dr. Sarah Mitchell',
    submittedByType: 'institution',
    institution: 'University of Technology',
    submissionDate: '2024-11-15',
    status: 'assessment-completed',
    category: 'Health',
    keywords: ['AI', 'Cancer Detection', 'Medical Imaging', 'Machine Learning'],
    stage: 'prototype',
    description: 'An artificial intelligence system that analyzes medical imaging to detect early-stage cancer with 95% accuracy, significantly improving early diagnosis rates.',
    targetUsers: 'Hospitals, diagnostic centers, oncology departments',
    documentUrl: '/mock-document.pdf',
    reviewerId: 'REV-001',
    reviewerName: 'Dr. James Chen',
    decision: 'has-potential',
    ipProtectionTypes: ['Patent', 'Copyright'],
    ipProtectionRecommendation: 'This innovation has strong potential for Intellectual Property protection. Patent protection is recommended for the AI algorithm, diagnostic methodology, and system architecture. Copyright protection should be applied to the software code and documentation.',
    improvementRecommendations: 'Expand dataset diversity, validate across multiple cancer types, develop user-friendly interface for clinicians.',
    assessmentMethod: 'manual',
    aiRelevanceScore: 98
  },
  {
    id: 'INV-2024-002',
    title: 'Smart Urban Waste Management System',
    submittedBy: 'GreenTech Innovations Ltd.',
    submittedByType: 'individual',
    submissionDate: '2024-11-18',
    status: 'pending-assessment',
    category: 'Tech',
    keywords: ['IoT', 'Sustainability', 'Smart City', 'Waste Management'],
    stage: 'pilot',
    description: 'IoT-enabled waste bins with fill-level sensors and route optimization software to reduce collection costs by 40% and improve urban cleanliness.',
    targetUsers: 'Municipal governments, waste management companies, smart cities',
    documentUrl: '/mock-document.pdf',
    aiRelevanceScore: 92
  },
  {
    id: 'INV-2024-003',
    title: 'Adaptive Learning Platform for STEM Education',
    submittedBy: 'Prof. Michael Roberts',
    submittedByType: 'institution',
    institution: 'State University',
    submissionDate: '2024-11-20',
    status: 'assessment-completed',
    category: 'Education',
    keywords: ['EdTech', 'Adaptive Learning', 'STEM', 'Personalization'],
    stage: 'prototype',
    description: 'An adaptive learning platform that personalizes STEM curriculum based on student performance, learning pace, and cognitive patterns.',
    targetUsers: 'K-12 schools, universities, online education platforms',
    documentUrl: '/mock-document.pdf',
    reviewerId: 'REV-002',
    reviewerName: 'Dr. Emily Watson',
    decision: 'no-potential',
    ipProtectionRecommendation: 'This innovation has limited potential for Intellectual Property protection due to significant prior art in the adaptive learning space. Copyright protection may apply to original content and software code, but the core methodology lacks novelty for patent protection.',
    improvementRecommendations: 'Identify unique value proposition that differentiates from existing solutions. Focus on novel algorithmic approaches or specific underserved educational segments.',
    assessmentMethod: 'ai',
    aiRelevanceScore: 75
  },
  {
    id: 'INV-2024-004',
    title: 'Biodegradable Packaging from Agricultural Waste',
    submittedBy: 'EcoPackage Solutions',
    submittedByType: 'individual',
    submissionDate: '2024-11-21',
    status: 'ip-filed',
    category: 'Manufacturing',
    keywords: ['Sustainability', 'Biodegradable', 'Packaging', 'Agriculture'],
    stage: 'market-ready',
    description: 'Manufacturing process to convert rice husks and wheat straw into durable, fully biodegradable packaging materials that decompose in 90 days.',
    targetUsers: 'Food industry, e-commerce companies, consumer goods manufacturers',
    documentUrl: '/mock-document.pdf',
    reviewerId: 'REV-001',
    reviewerName: 'Dr. James Chen',
    decision: 'has-potential',
    ipProtectionTypes: ['Patent', 'Industrial Design'],
    ipProtectionRecommendation: 'This innovation has excellent potential for Intellectual Property protection. Patent protection is strongly recommended for the manufacturing process, material composition, and treatment methods. Industrial Design protection should be considered for unique packaging shapes and forms.',
    improvementRecommendations: 'Scale production capacity, obtain relevant certifications, establish quality consistency metrics.',
    assessmentMethod: 'manual',
    aiRelevanceScore: 96,
    filingRequest: {
      selectedTypes: ['Patent', 'Industrial Design'],
      notifiedToReviewer: true,
      notificationDate: '2024-11-25'
    }
  },
  {
    id: 'INV-2024-005',
    title: 'Blockchain-Based Supply Chain Transparency Platform',
    submittedBy: 'TechVenture Institute',
    submittedByType: 'institution',
    institution: 'Innovation Hub College',
    submissionDate: '2024-11-22',
    status: 'pending-assessment',
    category: 'Tech',
    keywords: ['Blockchain', 'Supply Chain', 'Transparency', 'Traceability'],
    stage: 'prototype',
    description: 'A blockchain platform enabling end-to-end supply chain transparency, allowing consumers to trace product origins and verify authenticity.',
    targetUsers: 'Luxury goods, pharmaceuticals, food & beverage industries',
    documentUrl: '/mock-document.pdf',
    aiRelevanceScore: 88
  },
  {
    id: 'INV-2024-006',
    title: 'Renewable Energy Micro-Grid Controller',
    submittedBy: 'Dr. Ahmed Al-Mansouri',
    submittedByType: 'institution',
    institution: 'University of Technology',
    submissionDate: '2024-11-10',
    status: 'pending-assessment',
    category: 'Tech',
    keywords: ['Renewable Energy', 'Smart Grid', 'Energy Management', 'IoT'],
    stage: 'pilot',
    description: 'Advanced controller system for managing micro-grids powered by solar and wind, optimizing energy distribution and storage.',
    targetUsers: 'Rural communities, remote facilities, developing nations, disaster relief',
    documentUrl: '/mock-document.pdf',
    aiRelevanceScore: 94
  }
];

export const getCategoryIcon = (category: string) => {
  const icons: { [key: string]: string } = {
    'Tech': 'Cpu',
    'Health': 'Stethoscope',
    'Education': 'GraduationCap',
    'Manufacturing': 'Factory',
    'Sustainability': 'Leaf'
  };
  return icons[category] || 'Lightbulb';
};

export const categories = ['Tech', 'Health', 'Education', 'Manufacturing', 'Sustainability'];
export const stages = ['prototype', 'pilot', 'market-ready'];
export const institutions = ['University of Technology', 'State University', 'Innovation Hub College'];
